/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DIAGRAMME - die rechnende Hälfte (TX3, TX-STANDARD S1.8)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Skalen, „schöne" Achsenteilung, Pfade, Lastdauerlinie, Verdichtung - ohne
 * React und ohne Bibliothek (S1.8: kein neues Paket), deshalb einzeln
 * prüfbar. Gezeichnet wird in `Diagramm.tsx` als SVG.
 *
 * FESTLEGUNGEN
 * ------------
 * - Fehlstellen (`null`, NaN, ±∞) werden NICHT überbrückt: Eine Linie hat
 *   dort eine Lücke, ein Balken fehlt, die Wertetabelle sagt „—". Eine
 *   gerade Verbindung über ein Loch sähe aus wie ein Messwert.
 * - Viele Werte (Jahresgang 8760 h) werden für die DARSTELLUNG verdichtet
 *   (Minimum, Maximum, Mittel je Tag bzw. je Bildspalte) - die Wertetabelle
 *   zeigt die echten Werte (Auflage main 18.10.2026). Ein Pfad mit 8760
 *   Punkten ist nicht nötig, um 720 Bildpunkte zu füllen.
 * - Farben in fester Reihenfolge (`--diagramm-1…8`), dazu je Reihe eine
 *   Strichart: Die Reihe bleibt in Schwarzweiß und bei Farbfehlsicht
 *   unterscheidbar.
 */

export const REIHEN_HOECHSTENS = 8;

/** Strichart je Reihe (SVG `stroke-dasharray`), in derselben festen Reihenfolge wie die Farben. */
export const STRICHARTEN = ['', '6 3', '2 3', '8 3 2 3', '4 4', '10 3', '1 3', '6 2 2 2'] as const;

export function istZahl(w: unknown): w is number {
  return typeof w === 'number' && Number.isFinite(w);
}

/** Lineare Abbildung eines Wertebereichs auf einen Bildbereich. */
export function skala(von: number, bis: number, bildVon: number, bildBis: number) {
  const spanne = bis - von || 1;
  return (w: number) => bildVon + ((w - von) / spanne) * (bildBis - bildVon);
}

/**
 * Teilung einer Achse in „schöne" Schritte (1, 2, 2,5, 5 × 10ⁿ), mit
 * mindestens dem Datenbereich. `nullEinschliessen`: Leistungen und Mengen
 * beginnen bei 0 - eine abgeschnittene Achse übertreibt Unterschiede.
 */
export function achsenteilung(
  min: number,
  max: number,
  { ziel = 5, nullEinschliessen = true }: { ziel?: number; nullEinschliessen?: boolean } = {},
): { von: number; bis: number; schritt: number; marken: number[] } {
  let a = Math.min(min, max);
  let b = Math.max(min, max);
  if (nullEinschliessen) { a = Math.min(0, a); b = Math.max(0, b); }
  if (!Number.isFinite(a) || !Number.isFinite(b)) { a = 0; b = 1; }
  if (a === b) { b = a === 0 ? 1 : a + Math.abs(a) * 0.1; }

  const roh = (b - a) / Math.max(1, ziel);
  const zehner = 10 ** Math.floor(Math.log10(roh));
  const stufe = [1, 2, 2.5, 5, 10].find((s) => s * zehner >= roh) ?? 10;
  const schritt = stufe * zehner;
  const von = Math.floor(a / schritt + 1e-9) * schritt;
  const bis = Math.ceil(b / schritt - 1e-9) * schritt;
  const marken: number[] = [];
  for (let w = von; w <= bis + schritt / 1e6; w += schritt) marken.push(Number(w.toPrecision(12)));
  return { von, bis, schritt, marken };
}

/** Kleinster und größter Wert aller Reihen (ohne Fehlstellen); `null` ohne einen Wert. */
export function bereich(reihen: readonly (readonly (number | null)[])[]): { min: number; max: number } | null {
  let min = Infinity;
  let max = -Infinity;
  for (const r of reihen) for (const w of r) if (istZahl(w)) { if (w < min) min = w; if (w > max) max = w; }
  return min === Infinity ? null : { min, max };
}

/**
 * SVG-Pfad einer Linie; jede Fehlstelle beendet einen Abschnitt (`M` neu),
 * statt sie zu überbrücken.
 */
export function linienpfad(
  werte: readonly (number | null)[],
  x: (i: number) => number,
  y: (w: number) => number,
): string {
  let d = '';
  let neu = true;
  werte.forEach((w, i) => {
    if (!istZahl(w)) { neu = true; return; }
    d += `${neu ? 'M' : 'L'}${runde(x(i))},${runde(y(w))}`;
    neu = false;
  });
  return d;
}

/** Bildkoordinaten auf 0,1 px - kürzere Pfade, gleiches Bild. */
export function runde(z: number): number {
  return Math.round(z * 10) / 10;
}

/** Ergebnis der Verdichtung je Abschnitt. */
export interface Abschnitt {
  von: number; // erster Index (einschließlich)
  bis: number; // letzter Index (ausschließlich)
  min: number | null;
  max: number | null;
  mittel: number | null;
  fehlend: number;
}

/**
 * Verdichtet eine lange Reihe in Abschnitte gleicher Länge (z. B. 24 h = ein
 * Tag) - nach der Fehlstellenregel: Fehlstellen gehen in keinen Wert ein und
 * werden gezählt; ein Abschnitt ohne Wert hat `null`.
 */
export function verdichte(werte: readonly (number | null)[], laenge: number): Abschnitt[] {
  const n = Math.max(1, Math.floor(laenge));
  const aus: Abschnitt[] = [];
  for (let von = 0; von < werte.length; von += n) {
    const bis = Math.min(werte.length, von + n);
    let min = Infinity; let max = -Infinity; let summe = 0; let gezaehlt = 0; let fehlend = 0;
    for (let i = von; i < bis; i += 1) {
      const w = werte[i];
      if (!istZahl(w)) { fehlend += 1; continue; }
      gezaehlt += 1; summe += w;
      if (w < min) min = w;
      if (w > max) max = w;
    }
    aus.push({
      von, bis, fehlend,
      min: gezaehlt ? min : null, max: gezaehlt ? max : null, mittel: gezaehlt ? summe / gezaehlt : null,
    });
  }
  return aus;
}

/**
 * Verdichtet auf höchstens `spalten` Bildspalten (Minimum und Maximum je
 * Spalte): Die Hülle bleibt, Spitzen gehen nicht verloren.
 */
export function verdichteAufSpalten(werte: readonly (number | null)[], spalten: number): Abschnitt[] {
  if (werte.length <= spalten) return verdichte(werte, 1);
  return verdichte(werte, Math.ceil(werte.length / Math.max(1, spalten)));
}

/** SVG-Pfad eines Bandes (Fläche zwischen Minimum und Maximum je Abschnitt); Lücken trennen Teilflächen. */
export function bandpfad(
  abschnitte: readonly Abschnitt[],
  x: (i: number) => number,
  y: (w: number) => number,
): string {
  let d = '';
  let lauf: { i: number; a: Abschnitt }[] = [];
  const schliesse = () => {
    if (lauf.length === 0) return;
    const oben = lauf.map(({ i, a }) => `${runde(x(i))},${runde(y(a.max!))}`);
    const unten = [...lauf].reverse().map(({ i, a }) => `${runde(x(i))},${runde(y(a.min!))}`);
    d += `M${oben.join('L')}L${unten.join('L')}Z`;
    lauf = [];
  };
  abschnitte.forEach((a, i) => {
    if (a.min === null || a.max === null) { schliesse(); return; }
    lauf.push({ i, a });
  });
  schliesse();
  return d;
}

/**
 * Lastdauerlinie: die Werte absteigend geordnet (Fehlstellen fallen weg und
 * werden gezählt), dazu die Kennwerte, die man aus ihr abliest.
 *
 * - `volllaststunden` = Σ Werte / Höchstwert (nur mit Höchstwert > 0);
 * - `stundenUeber(schwelle)` = Zahl der Werte > Schwelle.
 */
export function lastdauer(werte: readonly (number | null)[]): {
  geordnet: number[];
  fehlend: number;
  hoechstwert: number | null;
  summe: number;
  volllaststunden: number | null;
  stundenUeber: (schwelle: number) => number;
} {
  const geordnet = werte.filter(istZahl).sort((a, b) => b - a);
  const fehlend = werte.length - geordnet.length;
  let summe = 0;
  for (const w of geordnet) summe += w;
  const hoechstwert = geordnet.length ? geordnet[0] : null;
  return {
    geordnet,
    fehlend,
    hoechstwert,
    summe,
    volllaststunden: hoechstwert !== null && hoechstwert > 0 ? summe / hoechstwert : null,
    stundenUeber: (s: number) => {
      // geordnet ist absteigend: zählen, bis der erste Wert <= s kommt.
      let n = 0;
      while (n < geordnet.length && geordnet[n] > s) n += 1;
      return n;
    },
  };
}

export const MONATE = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'] as const;
export const MONATE_LANG = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September',
  'Oktober', 'November', 'Dezember'] as const;

/** Tage je Monat eines Gemeinjahres (8760 h); im Schaltjahr hat der Februar 29 (8784 h). */
export function monatstage(schaltjahr: boolean): number[] {
  return [31, schaltjahr ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
}

/** Zu einer Stunde des Jahres (0-basiert) Monat (0-11), Tag im Monat (1-31) und Stunde (0-23). */
export function stundeImJahr(h: number, schaltjahr = false): { monat: number; tag: number; stunde: number } {
  let tag = Math.floor(h / 24);
  const stunde = h % 24;
  const tage = monatstage(schaltjahr);
  let monat = 0;
  while (monat < 11 && tag >= tage[monat]) { tag -= tage[monat]; monat += 1; }
  return { monat, tag: tag + 1, stunde };
}

/** Die Stundenwerte eines Jahres je Monat verdichtet (Summe, Mittel, Min, Max nach Fehlstellenregel). */
export function jeMonat(werte: readonly (number | null)[], schaltjahr = false): (Abschnitt & { summe: number | null })[] {
  const tage = monatstage(schaltjahr);
  const aus: (Abschnitt & { summe: number | null })[] = [];
  let von = 0;
  for (const t of tage) {
    const bis = Math.min(werte.length, von + t * 24);
    const [a] = verdichte(werte.slice(von, bis), Math.max(1, bis - von));
    const teil = werte.slice(von, bis).filter(istZahl);
    let s = 0;
    for (const w of teil) s += w;
    aus.push({
      ...(a ?? { min: null, max: null, mittel: null, fehlend: 0 }),
      von, bis, summe: teil.length ? s : null,
    });
    von = bis;
  }
  return aus;
}
